import type { CatalogMatch } from "./spokenCatalogMatch";
import { normalizeCatalogName } from "./spokenCatalogMatch";
import type { MfgInventoryCountLine } from "./types/inventoryCount";

export type SpokenCountApplyRow = {
  lineId: string;
  qty: number;
  materialName: string;
};

export type InventoryCountDictateDraftLine = {
  key: string;
  lineId: string | null;
  materialName: string;
  qty: string;
  match: CatalogMatch["status"];
};

export function applySpokenCountToLines(
  lines: readonly MfgInventoryCountLine[],
  matches: readonly CatalogMatch[],
): SpokenCountApplyRow[] {
  const applied: SpokenCountApplyRow[] = [];
  const used = new Set<string>();
  for (const match of matches) {
    if (match.status === "unmatched") continue;
    const byId = match.candidate
      ? lines.find((l) => l.material_id === match.candidate?.id && !used.has(l.id))
      : undefined;
    const byName = lines.find((l) => {
      if (used.has(l.id)) return false;
      const name = normalizeCatalogName(l.material_name ?? "");
      const spoken = normalizeCatalogName(match.spoken.name);
      const cand = normalizeCatalogName(match.candidate?.name ?? "");
      return name === spoken || (cand && name === cand);
    });
    const line = byId ?? byName;
    if (!line) continue;
    used.add(line.id);
    applied.push({
      lineId: line.id,
      qty: match.spoken.qty,
      materialName: line.material_name ?? match.spoken.name,
    });
  }
  return applied;
}

/** Review rows for the acceptance layer — sheet qty is not written until Apply. */
export function draftSpokenCountLines(
  lines: readonly MfgInventoryCountLine[],
  matches: readonly CatalogMatch[],
  makeKey: () => string = () => crypto.randomUUID(),
): InventoryCountDictateDraftLine[] {
  const applied = applySpokenCountToLines(lines, matches);
  let appliedAt = 0;
  return matches.map((match) => {
    if (match.status === "unmatched") {
      return {
        key: makeKey(),
        lineId: null,
        materialName: match.spoken.name,
        qty: String(match.spoken.qty),
        match: match.status,
      };
    }
    const row = applied[appliedAt];
    if (row) appliedAt += 1;
    return {
      key: makeKey(),
      lineId: row?.lineId ?? null,
      materialName: row?.materialName ?? match.candidate?.name ?? match.spoken.name,
      qty: String(match.spoken.qty),
      match: row ? match.status : "unmatched",
    };
  });
}
